import { useEffect, useMemo } from "react";
import {
  SecurityContributionsTable,
  SecurityContributionsTableProps,
  ContributionData,
} from "./contributions-table";
import { useSecuritiesPerformance } from "~/hooks/use-securities-performance";

interface SecurityContributionsWithPerformanceProps
  extends Omit<SecurityContributionsTableProps, "data"> {
  data: Record<string, ContributionData>;
  timeframe?: "1M" | "3M" | "6M" | "1Y";
  showPerformance?: boolean; // Control whether to show performance data
}

/**
 * Wrapper component that fetches performance data and enriches
 * the securities table with Performance 1M (%) column
 */
export function SecurityContributionsWithPerformance({
  data,
  timeframe = "1M",
  showPerformance = true,
  ...props
}: SecurityContributionsWithPerformanceProps) {
  // Extract FIGIs from data keys and create stable key
  const figis = useMemo(() => Object.keys(data), [data]);
  const figisKey = useMemo(() => [...figis].sort().join(","), [figis]);

  // Fetch performance data and timeseries for all securities
  const {
    performanceData,
    timeseriesData,
    fetchPerformance,
  } = useSecuritiesPerformance({
    figis,
    timeframe,
    autoFetch: false, // Disable auto-fetch, we'll control it manually
  });

  // Manual fetch with deduplication based on stable figisKey
  useEffect(() => {
    if (!showPerformance) return;

    // Small delay to batch potential duplicate calls from React strict mode
    const timer = setTimeout(() => {
      fetchPerformance();
    }, 50);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [figisKey, showPerformance, timeframe]); // Use stable figisKey instead of figis array

  // Enrich data with performance information and timeseries
  const enrichedData = useMemo(() => {
    if (!showPerformance) {
      return data;
    }

    const result: Record<string, ContributionData> = {};

    Object.entries(data).forEach(([figi, contributionData]) => {
      result[figi] = {
        ...contributionData,
        performance_1m: performanceData.get(figi) ?? null,
        timeseries: timeseriesData.get(figi) ?? undefined,
      };
    });

    return result;
  }, [data, performanceData, timeseriesData, showPerformance]);

  return <SecurityContributionsTable {...props} data={enrichedData} />;
}
